<template>
  <div class="pdf-print-component">
    <!-- Content to be printed/downloaded -->
    <div ref="printContent" class="print-content">
      <slot></slot>

      <!-- Print Footer (only visible when printing) -->
      <div class="print-footer">
        <p>Generated on: {{ currentDate }}</p>
        <p>UK Pension Entitlement Report</p>
      </div>
    </div>

    <!-- Action Buttons -->
    <!-- <div class="d-flex align-center justify-center gap-10" :class="{ 'print-only': hideButtons }">
      <VBtn
        color="primary"
        flat
        prepend-icon="mdi-download"
        @click="downloadPDF"
        :loading="isDownloading"
        :disabled="isDownloading"
      >
        Download PDF
      </VBtn>

      <VBtn
        flat
        color="secondary"
        variant="outlined"
        prepend-icon="mdi-printer"
        @click="printPage"
        :disabled="isDownloading"
      >
        Print
      </VBtn>
    </div> -->

    <!-- Download Progress -->
    <VSnackbar v-model="showProgress" :timeout="-1" color="primary">
      <div class="d-flex align-center">
        <VProgressCircular indeterminate size="20" class="mr-2" />
        Generating PDF... {{ downloadProgress }}%
      </div>
    </VSnackbar>

    <!-- Success/Error Messages -->
    <VSnackbar v-model="showMessage" :timeout="3000" :color="messageColor">
      {{ message }}
    </VSnackbar>
  </div>
</template>

<script>
import html2pdf from 'html2pdf.js';

export default {
  name: 'PDFPrintComponent',
  props: {
    filename: {
      type: String,
      default: 'uk-pension-entitlement-report'
    },
    hideButtons: {
      type: Boolean,
      default: false
    },
    printTitle: {
      type: String,
      default: 'UK Pension Entitlement Report'
    }
  },
  data() {
    return {
      isDownloading: false,
      showProgress: false,
      downloadProgress: 0,
      showMessage: false,
      message: '',
      messageColor: 'success',
      currentDate: new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    };
  },
  methods: {
    // Print functionality
    printPage() {
      // eslint-disable-next-line no-unused-vars
      const printContent = this.$refs.printContent;

      // Create print styles
      const style = document.createElement('style');
      style.innerHTML = `
        @media print {
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 20px;
          }
          .action-buttons {
            display: none !important;
          }
          .print-footer {
            position: fixed;
            bottom: 20px;
            right: 20px;
            font-size: 10px;
            color: #666;
            text-align: right;
          }
          .v-btn, button.main-btn, button.green-btn, button.white-btn {
            background-color: #f0f0f0 !important;
            color: #000 !important;
            border: 1px solid #999 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .table {
            border-collapse: collapse;
            width: 100%;
          }
          .table td, .table th {
            border: 1px solid #ddd;
            padding: 8px;
          }
        }
      `;
      document.head.appendChild(style);

      // Print
      window.print();

      // Remove style after printing
      setTimeout(() => {
        document.head.removeChild(style);
      }, 1000);
    },
    // В методе downloadPDF компонента PDFPrintComponent
    async downloadPDF() {
      this.isDownloading = true;
      this.showProgress = true;
      this.downloadProgress = 0;

      try {
        const element = this.$refs.printContent;

        // Clone the element to avoid modifying the original
        const clone = element.cloneNode(true);

        // Добавляем инлайн-стили для основных элементов
        this.injectInlineStyles(clone);

        // Add print-friendly styles to clone
        clone.classList.add('pdf-version');

        // Create a temporary container
        const container = document.createElement('div');
        container.appendChild(clone);
        container.style.padding = '0px';
        container.style.backgroundColor = 'white';
        container.style.fontFamily = 'Inter, sans-serif';
        document.body.appendChild(container);

        // PDF options with better configuration
        const options = {
          margin: [0.5, 0.5, 0.5, 0.5],
          filename: `${this.filename}-${this.currentDate.replace(/\s/g, '-')}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            letterRendering: true,
            useCORS: true,
            logging: true,
            allowTaint: false,
            backgroundColor: '#ffffff'
          },
          jsPDF: {
            unit: 'in',
            format: 'a4',
            orientation: 'portrait'
          },
          pagebreak: { mode: ['css', 'legacy'] }
        };

        // Generate PDF
        await html2pdf().set(options).from(container).save();

        // Clean up
        document.body.removeChild(container);

        this.showSuccess('PDF downloaded successfully!');
      } catch (error) {
        console.error('PDF generation error:', error);
        this.showError('Failed to generate PDF. Please try again.');
      } finally {
        this.isDownloading = false;
        this.showProgress = false;
        this.downloadProgress = 0;
      }
    },

    injectInlineStyles(element) {
      const tables = element.querySelectorAll('.table');
      tables.forEach((table) => {
        table.style.borderCollapse = 'collapse';
        table.style.width = '100%';
        table.style.margin = '15px 0';
        table.style.fontFamily = 'Arial, sans-serif';
        table.style.fontSize = '13px';

        const headers = table.querySelectorAll('th');
        headers.forEach((header) => {
          header.style.backgroundColor = '#f5f5f5';
          header.style.fontWeight = 'bold';
          header.style.padding = '8px 12px';
          header.style.border = '1px solid #ddd';
          header.style.textAlign = 'left';
        });

        const cells = table.querySelectorAll('td');
        cells.forEach((cell) => {
          cell.style.padding = '8px 12px';
          cell.style.border = '1px solid #ddd';
          cell.style.textAlign = 'left';
        });
      });

      const textMainColor = element.querySelectorAll('.text-main-color');
      textMainColor.forEach((el) => {
        el.style.color = '#179d88';
        el.style.fontWeight = 'bold';
      });

      const infoBlocks = element.querySelectorAll('.content__info');
      infoBlocks.forEach((block) => {
        block.style.padding = '15px';
        block.style.borderRadius = '12px';
        block.style.backgroundColor = '#f0fbf9';
        block.style.fontFamily = 'Inter, Arial, sans-serif';
        block.style.fontWeight = '700';
        block.style.fontSize = '17px';
        block.style.lineHeight = '24px';
        block.style.color = '#000000';
        block.style.margin = '10px 0';
      });

      const selectInputs = element.querySelectorAll('select');
      selectInputs.forEach((block) => {
        block.style.display = 'none';
      });

      const btns = element.querySelectorAll('button');
      btns.forEach((block) => {
        block.style.display = 'none';
      });

      const icons = element.querySelectorAll('.icon');
      icons.forEach((block) => {
        block.style.display = 'none';
      });

      const requestCheckBlock = element.querySelectorAll('.request-check');
      requestCheckBlock.forEach((block) => {
        block.style.display = 'none';
      });

      const titles = element.querySelectorAll('.content__title');
      titles.forEach((title) => {
        title.style.fontWeight = '700';
        title.style.fontSize = '20px';
        title.style.lineHeight = '100%';
        title.style.color = '#111827';
        title.style.margin = '15px 0 10px 0';
      });

      const texts = element.querySelectorAll('.content__text');
      texts.forEach((text) => {
        text.style.fontWeight = '600';
        text.style.fontSize = '16px';
        text.style.lineHeight = '22px';
        text.style.color = '#333333';
        text.style.margin = '5px 0';
      });

      const contetnWaring = element.querySelectorAll('.content__warning');
      contetnWaring.forEach((text) => {
        text.style.fontWeight = '600';
        text.style.fontSize = '16px';
        text.style.color = '#333333';
        text.style.margin = '5px 0';
        text.style.padding = '8px 12px';
        text.style.border = '1px solid #ddd';
        text.style.borderRadius = '10px';
      });

      const contetnWaringWithIcon = element.querySelectorAll('.content__warning-with-icon');
      contetnWaringWithIcon.forEach((text) => {
        text.style.fontWeight = '600';
        text.style.fontSize = '16px';
        text.style.color = '#333333';
        text.style.margin = '5px 0';
        text.style.padding = '8px 12px';
        text.style.border = '1px solid #ddd';
        text.style.borderRadius = '10px';
      });
    },

    showSuccess(message) {
      this.message = message;
      this.messageColor = 'success';
      this.showMessage = true;
    },

    showError(message) {
      this.message = message;
      this.messageColor = 'error';
      this.showMessage = true;
    }
  }
};
</script>

<style scoped>
.pdf-print-component {
  width: 100%;
}

.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-bottom: 20px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.print-content {
  background-color: white;
  padding: 0px;
  border-radius: 8px;
  margin: 10px 0;
}

.print-footer {
  display: none;
}

/* PDF specific styles */
:deep(.pdf-version) {
  font-family: Arial, sans-serif;
  line-height: 1.6;
  color: #333;
}

:deep(.pdf-version .text-main-color) {
  color: #1976d2 !important;
  font-weight: bold;
}

:deep(.pdf-version .table) {
  border-collapse: collapse;
  width: 100%;
  margin: 15px 0;
}

:deep(.pdf-version .table td),
:deep(.pdf-version .table th) {
  border: 1px solid #ddd;
  padding: 10px;
  text-align: left;
}

:deep(.pdf-version .table th) {
  background-color: #f5f5f5;
  font-weight: bold;
}

:deep(.pdf-version .main-btn),
:deep(.pdf-version .green-btn),
:deep(.pdf-version .white-btn) {
  padding: 8px 16px;
  border: 1px solid #1976d2;
  background-color: #1976d2;
  color: white;
  border-radius: 4px;
}

:deep(.pdf-version .white-btn) {
  background-color: white;
  color: #1976d2;
}

:deep(.pdf-version .green-btn) {
  background-color: #4caf50;
  border-color: #4caf50;
}

/* Hide action buttons when printing */
@media print {
  .action-buttons {
    display: none !important;
  }
}
</style>
